import type { LayerASpec, LayerAWalkStrip } from "@cold-case/shared/case-manifest";
import {
  BUILDING_FOOTPRINT_TILES,
  LAYER_A_GRID_H,
  LAYER_A_GRID_W,
  LAYER_A_MARGIN_TILES,
} from "@cold-case/shared/constants";
import {
  createSpineDistanceSampler,
  footprintTowardRoad,
  stampBuildingApproachPaths,
  stampBuildingPerimeterFence,
  stampBuildingRowBackFence,
  tileInWalkStrip,
  walkCorridorHalfWidth,
} from "@cold-case/shared/layer-walk";
import Phaser from "phaser";

import {
  bakeGroundCanvasRegion,
  groundChunkTileSpans,
  groundTextureCache,
} from "./map-ground-bake.js";
import { TILE_SIZE, TILES_H, TILES_W } from "./map-world-dims.js";

export type ZoneId = "downtown" | "maple_heights";

const GROUND_TEX_VERSION = "v18";

function bindCanvasTexture(
  scene: Phaser.Scene,
  texKey: string,
  canvas: HTMLCanvasElement
): void {
  if (scene.textures.exists(texKey)) {
    const tex = scene.textures.get(texKey);
    const src = tex.getSourceImage();
    if (src instanceof HTMLCanvasElement) {
      if (src !== canvas) {
        src.width = canvas.width;
        src.height = canvas.height;
        const ctx = src.getContext("2d");
        if (ctx) ctx.drawImage(canvas, 0, 0);
      }
      tex.setFilter(Phaser.Textures.FilterMode.LINEAR);
      tex.refresh();
      return;
    }
    scene.textures.remove(texKey);
  }
  scene.textures.addCanvas(texKey, canvas);
  scene.textures.get(texKey)?.setFilter(Phaser.Textures.FilterMode.LINEAR);
}

export function resolveWalkStrip(layerA: LayerASpec, zone: ZoneId): LayerAWalkStrip {
  const primary = zone === "downtown" ? layerA.downtown_walk : layerA.suburb_walk;
  if (primary) return primary;
  const other = zone === "downtown" ? layerA.suburb_walk : layerA.downtown_walk;
  if (other) return other;
  return {
    tile_x0: LAYER_A_MARGIN_TILES,
    tile_y0: LAYER_A_MARGIN_TILES,
    tile_x1: LAYER_A_GRID_W - LAYER_A_MARGIN_TILES,
    tile_y1: LAYER_A_GRID_H - LAYER_A_MARGIN_TILES,
  };
}

export class MapGrid {
  readonly walkable: boolean[][];
  private readonly strip: LayerAWalkStrip;
  private readonly distToSpine: (fx: number, fy: number) => number;
  private readonly buildingFootprints: { x0: number; y0: number; x1: number; y1: number }[] =
    [];

  constructor(
    public readonly zone: ZoneId,
    private readonly scene: Phaser.Scene,
    public readonly layerA: LayerASpec
  ) {
    const strip = resolveWalkStrip(layerA, zone);
    this.strip = strip;
    this.distToSpine = createSpineDistanceSampler(strip);
    this.walkable = [];
    for (let y = 0; y < TILES_H; y++) {
      const row: boolean[] = [];
      for (let x = 0; x < TILES_W; x++) {
        row.push(false);
      }
      this.walkable.push(row);
    }

    for (let ty = 0; ty < TILES_H; ty++) {
      for (let tx = 0; tx < TILES_W; tx++) {
        if (tileInWalkStrip(strip, tx, ty)) {
          this.walkable[ty]![tx] = true;
        }
      }
    }

    const zoneBuildings = (layerA.building_placements ?? []).filter((p) => p.zone === zone);
    for (const p of zoneBuildings) {
      this.buildingFootprints.push({
        x0: p.tile_x,
        y0: p.tile_y,
        x1: p.tile_x + BUILDING_FOOTPRINT_TILES,
        y1: p.tile_y + BUILDING_FOOTPRINT_TILES,
      });
    }
    stampBuildingApproachPaths(this.walkable, strip, zoneBuildings, BUILDING_FOOTPRINT_TILES);
    stampBuildingPerimeterFence(this.walkable, strip, zoneBuildings, BUILDING_FOOTPRINT_TILES, 2);
    stampBuildingRowBackFence(this.walkable, strip, zoneBuildings, BUILDING_FOOTPRINT_TILES, 2);
    this.stampSidewalkFrontPads(strip, zoneBuildings);
  }

  /** One-tile glue on the road-facing side only — reduces jitter approaching façades. */
  private stampSidewalkFrontPads(
    strip: LayerAWalkStrip,
    placements: readonly { tile_x: number; tile_y: number }[]
  ): void {
    const fp = BUILDING_FOOTPRINT_TILES;
    const h = this.walkable.length;
    const w = this.walkable[0]?.length ?? 0;
    const nearestFoot = (tx: number, ty: number) => {
      let best: { tile_x: number; tile_y: number } | null = null;
      let bestD = Infinity;
      for (const p of placements) {
        const fcx = p.tile_x + fp / 2;
        const fcy = p.tile_y + fp / 2;
        const d = Math.abs(tx + 0.5 - fcx) + Math.abs(ty + 0.5 - fcy);
        if (d < bestD) {
          bestD = d;
          best = p;
        }
      }
      return best;
    };
    for (let ty = 0; ty < h; ty++) {
      for (let tx = 0; tx < w; tx++) {
        if (this.walkable[ty]![tx]) continue;
        const foot = nearestFoot(tx, ty);
        if (!foot) continue;
        const toward = footprintTowardRoad(strip, foot.tile_x, foot.tile_y, fp);
        const fcx = foot.tile_x + fp / 2;
        const fcy = foot.tile_y + fp / 2;
        const relX = tx + 0.5 - fcx;
        const relY = ty + 0.5 - fcy;
        if (relX * toward.dx + relY * toward.dy <= 0.35) continue;
        for (const [dx, dy] of [
          [1, 0],
          [-1, 0],
          [0, 1],
          [0, -1],
        ] as const) {
          const nx = tx + dx;
          const ny = ty + dy;
          if (nx < 0 || ny < 0 || nx >= w || ny >= h) continue;
          if (this.walkable[ny]![nx]) {
            this.walkable[ty]![tx] = true;
            break;
          }
        }
      }
    }
  }

  render(): Phaser.GameObjects.Container {
    const hPx = TILES_H * TILE_SIZE;
    const strip = this.strip;
    const sk =
      strip.spine_tx && strip.spine_tx.length > 0
        ? `${strip.spine_tx[0]}_${strip.spine_tx[strip.spine_tx.length - 1]}_${strip.spine_tx.length}`
        : "nor";
    const downtown = this.zone === "downtown";
    const half = walkCorridorHalfWidth(strip);
    const container = this.scene.add.container(0, 0);

    for (const { txMin, txMax } of groundChunkTileSpans()) {
      const wPx = (txMax - txMin) * TILE_SIZE;
      const texKey = `ground_diag_${this.zone}_${sk}_${txMin}_${txMax}_${hPx}_${GROUND_TEX_VERSION}`;
      let canvas = groundTextureCache.get(texKey);
      if (!canvas) {
        const zoneAccents = (this.layerA.accents ?? []).filter((a) => a.zone === this.zone);
        canvas = bakeGroundCanvasRegion(
          this.distToSpine,
          half,
          downtown,
          this.buildingFootprints,
          txMin,
          txMax,
          zoneAccents
        );
        groundTextureCache.set(texKey, canvas);
      }
      bindCanvasTexture(this.scene, texKey, canvas);
      const tile = this.scene.add.tileSprite(txMin * TILE_SIZE, 0, wPx, hPx, texKey);
      tile.setOrigin(0, 0);
      container.add(tile);
    }

    this.addFenceHints(container);
    container.setDepth(0);
    return container;
  }

  /** Dark edge on blocked perimeter tiles so building fences read clearly on the ground. */
  private addFenceHints(container: Phaser.GameObjects.Container): void {
    const g = this.scene.add.graphics();
    g.fillStyle(0x14141f, 0.55);
    const inFootprint = (tx: number, ty: number) =>
      this.buildingFootprints.some(
        (fp) => tx >= fp.x0 && tx < fp.x1 && ty >= fp.y0 && ty < fp.y1
      );
    const touchesFootprint = (tx: number, ty: number) => {
      for (const [ox, oy] of [
        [1, 0],
        [-1, 0],
        [0, 1],
        [0, -1],
      ] as const) {
        if (inFootprint(tx + ox, ty + oy)) return true;
      }
      return false;
    };
    for (const fp of this.buildingFootprints) {
      for (let ty = fp.y0 - 3; ty <= fp.y1 + 2; ty++) {
        for (let tx = fp.x0 - 3; tx <= fp.x1 + 2; tx++) {
          if (this.isWalkableTile(tx, ty) || !touchesFootprint(tx, ty)) continue;
          g.fillRect(tx * TILE_SIZE + 2, ty * TILE_SIZE + 2, TILE_SIZE - 4, TILE_SIZE - 4);
        }
      }
    }
    g.setDepth(1);
    container.add(g);
  }

  isWalkableTile(tx: number, ty: number): boolean {
    if (tx < 0 || ty < 0 || tx >= TILES_W || ty >= TILES_H) return false;
    return this.walkable[ty]![tx]!;
  }

  pixelToTile(px: number, py: number): { tx: number; ty: number } {
    return {
      tx: Math.floor(px / TILE_SIZE),
      ty: Math.floor(py / TILE_SIZE),
    };
  }
}
